import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiX, FiDownload, FiExternalLink } from "react-icons/fi";
import * as S from "./MediaViewer.styles";

interface BaseProps {
  open: boolean;
  onClose: () => void;
  src: string;
  fileName?: string;
}

/**
 * Lightbox de imagem em fullscreen. Fecha com ESC, clique no backdrop ou
 * botão X. Botão de download abre a URL em nova aba.
 */
export function ImageLightbox({
  open,
  onClose,
  src,
  fileName,
}: BaseProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const node = (
    <S.Backdrop onClick={onClose}>
      <S.TopBar onClick={(e) => e.stopPropagation()}>
        {fileName && <S.FileName>{fileName}</S.FileName>}
        <S.IconBtn
          as="a"
          href={src}
          download={fileName || true}
          target="_blank"
          rel="noopener noreferrer"
          title="Baixar"
        >
          <FiDownload size={18} />
        </S.IconBtn>
        <S.IconBtn type="button" onClick={onClose} title="Fechar">
          <FiX size={20} />
        </S.IconBtn>
      </S.TopBar>
      <S.ImageWrap onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={fileName || ""} />
      </S.ImageWrap>
    </S.Backdrop>
  );

  return createPortal(node, document.body);
}

/**
 * Visualizador de PDF inline em modal. Renderiza o PDF via <iframe>; o
 * navegador usa seu visualizador nativo. Fecha com ESC, X ou clique no
 * backdrop. Botões para baixar ou abrir em nova aba.
 */
export function PdfViewerModal({
  open,
  onClose,
  src,
  fileName,
}: BaseProps): JSX.Element | null {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setLoaded(false);
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const node = (
    <S.Backdrop onClick={onClose}>
      <S.PdfPanel onClick={(e) => e.stopPropagation()}>
        <S.PdfHeader>
          <S.FileName as="span">{fileName || "Documento PDF"}</S.FileName>
          <S.HeaderActions>
            <S.IconBtn
              as="a"
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              title="Abrir em nova aba"
            >
              <FiExternalLink size={18} />
            </S.IconBtn>
            <S.IconBtn
              as="a"
              href={src}
              download={fileName || true}
              target="_blank"
              rel="noopener noreferrer"
              title="Baixar"
            >
              <FiDownload size={18} />
            </S.IconBtn>
            <S.IconBtn type="button" onClick={onClose} title="Fechar">
              <FiX size={20} />
            </S.IconBtn>
          </S.HeaderActions>
        </S.PdfHeader>
        <S.PdfBody>
          {!loaded && <S.Loading>Carregando documento...</S.Loading>}
          <iframe
            title={fileName || "PDF"}
            src={src}
            onLoad={() => setLoaded(true)}
          />
        </S.PdfBody>
      </S.PdfPanel>
    </S.Backdrop>
  );

  return createPortal(node, document.body);
}
